/** Shared sold-asset display logic (AssetTable, depreciation report, etc.) */

export const SOLD_DISPLAY_COLOR = '#c62828';
export const SOLD_MARKER = 'נמכר ב';
export const RELATES_TO_PREFIX = 'שייך לנכס ';

export const getAssetId = (asset) => asset?.id ?? asset?.Id ?? null;

const sameAssetId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
};

/**
 * When a root asset in an AssetRelation is sold, linked children should show as sold in UI
 * (AssetTable, depreciation report) via soldViaAssetGroup — without persisting sale fields on children.
 */
export function enrichAssetsWithGroupSaleInfo(assets, assetRelations) {
    if (!assets?.length) return assets || [];

    const byId = new Map();
    assets.forEach((a) => {
        const id = getAssetId(a);
        if (id != null) byId.set(String(id).toLowerCase(), { ...a, id });
    });

    (assetRelations || []).forEach((rel) => {
        const rootId = rel.rootAssetId || rel.rootAsset?.id;
        if (!rootId) return;

        const root = [...byId.values()].find((a) => sameAssetId(getAssetId(a), rootId));
        if (!root?.sellDate && !root?.saleIncomeId && !root?.saleJournalEntryId) return;

        const childIds = [
            ...(rel.relatedAssetIds || []),
            ...(rel.relatedAssets || []).map((c) => c?.id ?? c?.Id).filter(Boolean),
        ].filter((id) => id && !sameAssetId(id, rootId));

        [...new Set(childIds.map(String))].forEach((childIdStr) => {
            const child = [...byId.entries()].find(([key]) => sameAssetId(key, childIdStr))?.[1];
            if (!child) return;

            const childAlreadySold =
                !!child.sellDate || !!child.saleIncomeId || !!child.saleJournalEntryId;

            if (childAlreadySold) return;

            const childKey = String(getAssetId(child)).toLowerCase();
            byId.set(childKey, {
                ...child,
                sellDate: root.sellDate,
                saleIncomeId: root.saleIncomeId,
                saleJournalEntryId: root.saleJournalEntryId,
                sellAmount: child.sellAmount ?? null,
                soldViaAssetGroup: true,
            });
        });
    });

    let result = assets.map((a) => {
        const id = getAssetId(a);
        if (id == null) return a;
        return byId.get(String(id).toLowerCase()) || a;
    });

    result = applyDescriptionLinkedSaleFallback(result);

    return result;
};

/** Fallback when relations are missing: match "שייך לנכס {name}" to a sold root by name. */
function applyDescriptionLinkedSaleFallback(assets) {
    if (!assets?.length) return assets;

    const soldByName = new Map();
    assets.forEach((a) => {
        const name = (a?.name || '').trim();
        if (name && isAssetSold(a)) soldByName.set(name, a);
    });

    return assets.map((asset) => {
        if (isAssetSold(asset)) return asset;

        const desc = (asset?.description || '').trim();
        if (!desc.includes(RELATES_TO_PREFIX)) return asset;

        const afterPrefix = desc.slice(desc.indexOf(RELATES_TO_PREFIX) + RELATES_TO_PREFIX.length);
        const parentName = afterPrefix.split(' - ')[0].split(SOLD_MARKER)[0].trim();
        if (!parentName) return asset;

        const root = soldByName.get(parentName);
        if (!root?.sellDate) return asset;

        return {
            ...asset,
            sellDate: root.sellDate,
            saleIncomeId: root.saleIncomeId,
            saleJournalEntryId: root.saleJournalEntryId,
            sellAmount: asset.sellAmount ?? null,
            soldViaAssetGroup: true,
        };
    });
}

/** Batch-enrich report/table assets; returns Map(lowercaseId -> enriched asset). */
export function buildEnrichedAssetLookup(assets, assetRelations) {
    const enriched = enrichAssetsWithGroupSaleInfo(assets || [], assetRelations);
    const lookup = new Map();
    enriched.forEach((a) => {
        const id = getAssetId(a);
        if (id != null) lookup.set(String(id).toLowerCase(), a);
    });
    return lookup;
}

export function resolveEnrichedAsset(asset, assetRelations, allAssetsInPool = [], lookup = null) {
    if (!asset) return asset;
    const id = getAssetId(asset);
    if (lookup && id != null) {
        const fromLookup = lookup.get(String(id).toLowerCase());
        if (fromLookup) return fromLookup;
    }
    const pool = allAssetsInPool?.length ? allAssetsInPool : [asset];
    const enriched = enrichAssetsWithGroupSaleInfo(pool, assetRelations);
    return enriched.find((a) => sameAssetId(getAssetId(a), id)) || asset;
}

export function enrichAssetForSaleDisplay(asset, assetRelations, allAssetsInPool = null) {
    return resolveEnrichedAsset(asset, assetRelations, allAssetsInPool);
}

export function isAssetSold(asset) {
    return !!(asset?.sellDate || asset?.saleIncomeId || asset?.saleJournalEntryId);
}

export function isAssetSoldInYear(asset, reportYear) {
    if (reportYear == null || !asset?.sellDate) return false;
    return new Date(asset.sellDate).getFullYear() === reportYear;
}

/** Sold in report year — direct sale or child of sold root (after enrich). */
export function isAssetSoldForReportDisplay(asset, assetRelations, reportYear, allAssetsInPool = [], lookup = null) {
    const enriched = resolveEnrichedAsset(asset, assetRelations, allAssetsInPool, lookup);
    return isAssetSold(enriched) && isAssetSoldInYear(enriched, reportYear);
}

const formatDateDdMmYyyy = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
};

/** Formats description so linked children show "שייך לנכס X - נמכר ב:DD/MM/YYYY". */
export function formatAssetDescription(description, sellDate, isSold) {
    const desc = (description || '').trim();
    if (!isSold || !sellDate) return desc;

    const soldText = `${SOLD_MARKER}:${formatDateDdMmYyyy(sellDate)}`;

    if (desc.includes(SOLD_MARKER)) {
        if (desc.includes(RELATES_TO_PREFIX) && !desc.includes(' - ' + SOLD_MARKER) && !desc.includes(` ${SOLD_MARKER}`)) {
            const soldIdx = desc.indexOf(SOLD_MARKER);
            const relatesPart = desc.substring(0, soldIdx).trimEnd();
            const soldPart = desc.substring(soldIdx).trim();
            const normalizedSold = soldPart.startsWith(SOLD_MARKER) ? soldPart : soldText;
            return `${relatesPart} - ${normalizedSold}`;
        }
        return desc;
    }

    if (desc.includes(RELATES_TO_PREFIX)) {
        return `${desc} - ${soldText}`;
    }

    return desc ? `${desc} ${soldText}` : soldText;
}
